import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  Platform,
  RefreshControl,
  SectionList,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { DateTime } from 'luxon';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

import Animated, {
  useSharedValue,
  useAnimatedStyle,
  cancelAnimation,
  withRepeat,
  withSequence,
  withTiming,
  withSpring,
  withDelay,
  Easing,
  runOnJS,
} from 'react-native-reanimated';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { CalendarEvent } from '../../types/api';
import TimelineView from './TimelineView';
import WeekView from './WeekView';
import AgendaView from './AgendaView';
import EventChip from './EventChip';
import { useTheme, type ThemeColors } from '../../utils/theme';
import EmptyState from '../common/EmptyState';

const GRABBER_SEEN_KEY = 'cal_grabber_onboarded';

type TimeBlock = 'morning' | 'afternoon' | 'evening' | 'night' | 'allDay';
const TIME_BLOCK_ORDER: TimeBlock[] = ['morning', 'afternoon', 'evening', 'night', 'allDay'];
const TIME_BLOCK_ICONS: Record<TimeBlock, string> = {
  morning: 'sunny-outline',
  afternoon: 'partly-sunny-outline',
  evening: 'moon-outline',
  night: 'cloudy-night-outline',
  allDay: 'calendar-outline',
};

function getTimeBlock(event: { runAtUtc?: string }): TimeBlock {
  if (!event.runAtUtc) return 'allDay';
  const hour = DateTime.fromISO(event.runAtUtc).toLocal().hour;
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 21) return 'evening';
  return 'night';
}

function groupByTimeBlock(
  events: CalendarEvent[],
  t: TFunction
): Array<{ title: string; icon: string; data: CalendarEvent[] }> {
  const buckets: Record<TimeBlock, CalendarEvent[]> = {
    morning: [], afternoon: [], evening: [], night: [], allDay: [],
  };
  for (const ev of events) {
    buckets[getTimeBlock(ev)].push(ev);
  }
  const labels: Record<TimeBlock, string> = {
    morning: t('calendar.morning', 'Morning'),
    afternoon: t('calendar.afternoon', 'Afternoon'),
    evening: t('calendar.evening', 'Evening'),
    night: t('calendar.night', 'Night'),
    allDay: t('calendar.allDay', 'All Day'),
  };
  return TIME_BLOCK_ORDER
    .filter((b) => buckets[b].length > 0)
    .map((b) => ({ title: labels[b], icon: TIME_BLOCK_ICONS[b], data: buckets[b] }));
}

type ViewMode = 'month' | 'timeline' | 'week' | 'agenda';

interface DayNavigatorSheetProps {
  dayEvents: CalendarEvent[];
  allEvents?: CalendarEvent[];
  selectedDate: string;
  selectedWeekdayLabel: string;
  selectedDateDisplay: string;
  selectedDateLabel: string;
  viewMode: ViewMode;
  onChangeViewMode: (mode: ViewMode) => void;
  onPressEvent: (event: CalendarEvent) => void;
  onSelectDate?: (date: string) => void;
  onQuickAdd: () => void;
  onResizeEvent: (seq: number, durationMinutes: number) => void | Promise<void>;
  onMoveEvent: (seq: number, newRunAtUtc: string) => void | Promise<void>;
  onChangeColumn: (updates: Array<{ seq: number; colPref: number }>) => void | Promise<void>;
  onDragCreate?: (startMinutes: number, durationMinutes: number) => void;
  onNavigateDay: (direction: -1 | 1) => void;
  onNavigateWeek: (direction: -1 | 1) => void;
  onToggleSheet?: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  tagColors: Record<string, string> | null;
  bottomInset: number;
  headerGesture: React.ComponentProps<typeof GestureDetector>['gesture'];
  daySwipeGesture: React.ComponentProps<typeof GestureDetector>['gesture'];
  headerAnimatedStyle: any;
  contentAnimatedStyle: any;
  eventsSwipeAnimatedStyle: any;
  dragToDismissEnabled?: boolean;
  isPresented?: boolean;
  fullScreen?: boolean;
}

export default function DayNavigatorSheet({
  dayEvents,
  allEvents,
  selectedDate,
  selectedWeekdayLabel,
  selectedDateDisplay,
  selectedDateLabel,
  viewMode,
  onChangeViewMode,
  onPressEvent,
  onSelectDate,
  onQuickAdd,
  onResizeEvent,
  onMoveEvent,
  onChangeColumn,
  onDragCreate,
  onNavigateDay,
  onNavigateWeek,
  onToggleSheet,
  onRefresh,
  refreshing,
  tagColors,
  bottomInset,
  headerGesture,
  daySwipeGesture,
  headerAnimatedStyle,
  contentAnimatedStyle,
  eventsSwipeAnimatedStyle,
  dragToDismissEnabled = false,
  isPresented = true,
  fullScreen = false,
}: DayNavigatorSheetProps) {
  const { t, i18n } = useTranslation();
  const { colors } = useTheme();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [previewHeaderDate, setPreviewHeaderDate] = useState<string | null>(null);
  const effectiveHeaderDate = previewHeaderDate ?? selectedDate;
  const relativeDay = useMemo(() => {
    const selected = DateTime.fromISO(effectiveHeaderDate).startOf('day');
    const today = DateTime.local().startOf('day');
    if (!selected.isValid) return null;

    const dayDelta = Math.round(selected.diff(today, 'days').days);
    if (dayDelta === 0) {
      return {
        kind: 'today' as const,
        label: t('calendar.today', 'Today'),
        icon: 'sparkles' as const,
      };
    }
    if (dayDelta === 1) {
      return {
        kind: 'tomorrow' as const,
        label: t('calendar.tomorrow', 'Tomorrow'),
        icon: 'arrow-forward-circle' as const,
      };
    }
    if (dayDelta === -1) {
      return {
        kind: 'yesterday' as const,
        label: t('calendar.yesterday', 'Yesterday'),
        icon: 'time' as const,
      };
    }
    return null;
  }, [effectiveHeaderDate, t]);
  const headerDateTime = DateTime.fromISO(effectiveHeaderDate);
  const headerLocale = i18n.resolvedLanguage || i18n.language;
  const headerTitle = previewHeaderDate && headerDateTime.isValid
    ? headerDateTime.setLocale(headerLocale).toFormat('LLLL d')
    : selectedDateDisplay;
  const headerWeekdayLabel = previewHeaderDate && headerDateTime.isValid
    ? headerDateTime.setLocale(headerLocale).toFormat('cccc')
    : selectedWeekdayLabel;
  const headerEventCount = useMemo(() => {
    if (!previewHeaderDate) return dayEvents.length;
    return (allEvents || dayEvents).filter((event) => (
      !!event.runAtUtc
      && DateTime.fromISO(event.runAtUtc).toLocal().toISODate() === previewHeaderDate
    )).length;
  }, [allEvents, dayEvents, previewHeaderDate]);
  const sections = useMemo(() => groupByTimeBlock(dayEvents, t), [dayEvents, t]);
  const viewTransitionY = useSharedValue(0);
  const periodTransitionX = useSharedValue(0);
  const periodTransitionOpacity = useSharedValue(1);
  const sheetDragY = useSharedValue(0);
  const contentHadMultiplePointers = useSharedValue(false);
  const contentZoomActive = useSharedValue(0);
  const [weekHasHorizontalOverflow, setWeekHasHorizontalOverflow] = useState(false);
  const previousSelectedDateRef = useRef(selectedDate);

  useEffect(() => {
    // The parent date catches up when the native page settle completes. Clear
    // the optimistic header without changing what is painted on screen.
    setPreviewHeaderDate(null);
  }, [selectedDate]);

  const previewTimelineHeader = useCallback((direction: -1 | 1 | null) => {
    if (direction == null) {
      setPreviewHeaderDate(null);
      return;
    }
    const nextDate = DateTime.fromISO(selectedDate).plus({ days: direction }).toISODate();
    setPreviewHeaderDate(nextDate);
  }, [selectedDate]);

  useEffect(() => {
    if (isPresented) sheetDragY.value = 0;
  }, [isPresented, sheetDragY]);

  // Grabber pulse onboarding
  const grabberScale = useSharedValue(1);
  const tooltipOpacity = useSharedValue(0);

  useEffect(() => {
    AsyncStorage.getItem(GRABBER_SEEN_KEY).then((val) => {
      if (!val) {
        // Gentle pulse grabber 2 times
        grabberScale.value = withRepeat(
          withSequence(
            withTiming(1.25, { duration: 350, easing: Easing.out(Easing.cubic) }),
            withTiming(1, { duration: 350, easing: Easing.inOut(Easing.cubic) }),
          ),
          2,
          false
        );
        // Show tooltip briefly
        tooltipOpacity.value = withSequence(
          withTiming(1, { duration: 300 }),
          withDelay(2500, withTiming(0, { duration: 500 })),
        );
        AsyncStorage.setItem(GRABBER_SEEN_KEY, '1');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grabberAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scaleX: grabberScale.value }, { scaleY: grabberScale.value }],
  }));

  const tooltipAnimatedStyle = useAnimatedStyle(() => ({
    opacity: tooltipOpacity.value,
  }));

  const viewTransitionStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: viewTransitionY.value }],
  }));

  const periodTransitionStyle = useAnimatedStyle(() => ({
    opacity: periodTransitionOpacity.value,
    transform: [{ translateX: periodTransitionX.value }],
  }));

  const sheetDragStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: sheetDragY.value }],
  }));

  const dismissSheet = useCallback(() => {
    onToggleSheet?.();
  }, [onToggleSheet]);

  const sheetDismissGesture = useMemo(
    () => Gesture.Pan()
      .enabled(dragToDismissEnabled && !!onToggleSheet)
      .activeOffsetY(8)
      .failOffsetX([-24, 24])
      .onBegin(() => {
        cancelAnimation(sheetDragY);
      })
      .onUpdate((event) => {
        // Pointer events from Android emulators and some OEM touch stacks can
        // arrive unevenly. Interpolate larger gaps more than normal touch-sized
        // deltas so the panel stays responsive without visibly jumping.
        const nextDragY = Math.max(0, event.translationY);
        const followDistance = Math.abs(nextDragY - sheetDragY.value);
        const followDuration = Math.min(90, Math.max(16, followDistance * 0.45));
        sheetDragY.value = withTiming(nextDragY, {
          duration: followDuration,
          easing: Easing.linear,
        });
      })
      .onEnd((event) => {
        const shouldDismiss = event.translationY >= 64 || event.velocityY >= 520;
        if (shouldDismiss) {
          const dismissTarget = Math.max(windowHeight, 720);
          const remainingDistance = Math.max(0, dismissTarget - sheetDragY.value);
          const exitVelocity = Math.max(event.velocityY, 1200);
          const exitDuration = Math.min(320, Math.max(180, (remainingDistance / exitVelocity) * 1000));
          sheetDragY.value = withTiming(dismissTarget, {
            duration: exitDuration,
            easing: Easing.out(Easing.cubic),
          }, (finished) => {
            if (finished) runOnJS(dismissSheet)();
          });
          return;
        }
        sheetDragY.value = withSpring(0, {
          damping: 20,
          stiffness: 260,
          mass: 0.7,
          overshootClamping: true,
        });
      }),
    [dismissSheet, dragToDismissEnabled, onToggleSheet, sheetDragY, windowHeight],
  );

  const handleViewModeChange = useCallback((nextMode: ViewMode) => {
    if (nextMode === viewMode) return;
    viewTransitionY.value = 6;
    onChangeViewMode(nextMode);

    requestAnimationFrame(() => {
      viewTransitionY.value = withTiming(0, {
        duration: 150,
        easing: Easing.out(Easing.cubic),
      });
    });
  }, [onChangeViewMode, viewMode, viewTransitionY]);

  const animatePeriodEntry = useCallback((direction: -1 | 1) => {
    const travel = Math.min(76, Math.max(44, windowWidth * 0.16));
    periodTransitionX.value = direction * travel;
    periodTransitionOpacity.value = 0.82;
    requestAnimationFrame(() => {
      periodTransitionX.value = withTiming(0, {
        duration: 190,
        easing: Easing.out(Easing.cubic),
      });
      periodTransitionOpacity.value = withTiming(1, {
        duration: 150,
        easing: Easing.out(Easing.cubic),
      });
    });
  }, [periodTransitionOpacity, periodTransitionX, windowWidth]);

  const navigatePeriod = useCallback((direction: -1 | 1) => {
    if (viewMode === 'week') onNavigateWeek(direction);
    else onNavigateDay(direction);
  }, [onNavigateDay, onNavigateWeek, viewMode]);

  useEffect(() => {
    const previousDate = previousSelectedDateRef.current;
    previousSelectedDateRef.current = selectedDate;

    // Timeline animates its own native swipe so it can cooperate with vertical
    // scrolling, pinch zoom, and reminder dragging. Applying this sheet-level
    // transition as well caused a second slide and exposed the surface edges.
    if (viewMode === 'timeline') {
      periodTransitionX.value = 0;
      periodTransitionOpacity.value = 1;
      return;
    }
    if (previousDate === selectedDate) return;

    const previous = DateTime.fromISO(previousDate).startOf('day');
    const next = DateTime.fromISO(selectedDate).startOf('day');
    if (!previous.isValid || !next.isValid) return;
    if (viewMode === 'week' && previous.startOf('week').hasSame(next.startOf('week'), 'day')) {
      periodTransitionX.value = 0;
      periodTransitionOpacity.value = 1;
      return;
    }
    animatePeriodEntry(next < previous ? -1 : 1);
  }, [animatePeriodEntry, periodTransitionOpacity, periodTransitionX, selectedDate, viewMode]);

  const contentNavigationGesture = useMemo(
    () => Gesture.Pan()
      .enabled(!(viewMode === 'week' && weekHasHorizontalOverflow))
      .activeOffsetX([-18, 18])
      .failOffsetY([-28, 28])
      .maxPointers(1)
      .onTouchesDown((event) => {
        if (event.numberOfTouches === 1) contentHadMultiplePointers.value = false;
        else contentHadMultiplePointers.value = true;
      })
      .onTouchesMove((event) => {
        if (event.numberOfTouches > 1) contentHadMultiplePointers.value = true;
      })
      .onEnd((event) => {
        if (contentHadMultiplePointers.value || contentZoomActive.value > 0) return;
        const distanceReady = Math.abs(event.translationX) >= 52;
        const velocityReady = Math.abs(event.translationX) >= 18 && Math.abs(event.velocityX) >= 460;
        if (!distanceReady && !velocityReady) return;
        runOnJS(navigatePeriod)(event.translationX < 0 ? 1 : -1);
      })
      .onFinalize(() => {
        contentHadMultiplePointers.value = false;
      }),
    [contentHadMultiplePointers, contentZoomActive, navigatePeriod, viewMode, weekHasHorizontalOverflow],
  );

  const combinedContentSwipeGesture = useMemo(
    () => Gesture.Race(contentNavigationGesture, daySwipeGesture),
    [contentNavigationGesture, daySwipeGesture],
  );

  const detailContent = (
    <Animated.View style={[styles.contentWrap, fullScreen && styles.fullScreenContentWrap, eventsSwipeAnimatedStyle, contentAnimatedStyle, periodTransitionStyle]}>
      <Animated.View style={[styles.viewTransitionContent, viewTransitionStyle]}>
      {viewMode === 'timeline' ? (
        <View style={[styles.timelineWrap, fullScreen && styles.fullScreenContentSurface]}>
          <TimelineView
            events={dayEvents}
            allEvents={allEvents || dayEvents}
            selectedDate={selectedDate}
            onPressEvent={onPressEvent}
            onResizeEvent={onResizeEvent}
            onMoveEvent={onMoveEvent}
            onChangeColumn={onChangeColumn}
            onDragCreate={onDragCreate}
            onNavigateDay={onNavigateDay}
            onSwipeNavigationPreview={previewTimelineHeader}
            zoomGestureActive={contentZoomActive}
            tagColors={tagColors}
            bottomPadding={fullScreen ? 0 : undefined}
          />
        </View>
      ) : viewMode === 'week' ? (
        <View style={[styles.timelineWrap, fullScreen && styles.fullScreenContentSurface]}>
          <WeekView
            events={allEvents || dayEvents}
            selectedDate={selectedDate}
            onSelectDate={onSelectDate || (() => {})}
            onPressEvent={onPressEvent}
            onOpenDay={(date) => {
              onSelectDate?.(date);
              handleViewModeChange('timeline');
            }}
            onResizeEvent={onResizeEvent}
            onMoveEvent={onMoveEvent}
            onHorizontalOverflowChange={setWeekHasHorizontalOverflow}
            onNavigateWeek={onNavigateWeek}
            tagColors={tagColors}
            zoomGestureActive={contentZoomActive}
          />
        </View>
      ) : viewMode === 'agenda' ? (
        <View style={[styles.timelineWrap, fullScreen && styles.fullScreenContentSurface]}>
          <AgendaView
            events={allEvents || dayEvents}
            selectedDate={selectedDate}
            onPressEvent={onPressEvent}
            onSelectDate={onSelectDate}
            tagColors={tagColors}
          />
        </View>
      ) : (
        <SectionList
          sections={sections}
          renderSectionHeader={({ section }) => (
            <View style={styles.sectionHeader}>
              <Ionicons name={section.icon as any} size={14} color={colors.textDim} />
              <Text style={styles.sectionTitle}>{section.title}</Text>
            </View>
          )}
          renderItem={({ item, index }) => (
            <EventChip
              event={item}
              tagColors={tagColors}
              onPress={item.seq ? () => onPressEvent(item) : undefined}
              index={index}
              animateEntrance={false}
            />
          )}
          keyExtractor={(item, index) => `ev-${item.seq ?? index}`}
          extraData={tagColors}
          initialNumToRender={10}
          windowSize={5}
          maxToRenderPerBatch={10}
          updateCellsBatchingPeriod={50}
          removeClippedSubviews={Platform.OS === 'android'}
          ListEmptyComponent={
            <EmptyState
              emoji="📅"
              title={t('calendar.noEvents', 'No events')}
              subtitle={t('calendar.noEventsSubtitle', 'Select a date to see events')}
              actionLabel={t('calendar.quickAdd', 'Quick add reminder')}
              onAction={onQuickAdd}
            />
          }
          stickySectionHeadersEnabled={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} colors={[colors.primary]} />}
          style={[styles.eventsList, fullScreen && styles.fullScreenContentSurface]}
          contentContainerStyle={styles.listContent}
        />
      )}
      </Animated.View>
    </Animated.View>
  );

  // Timeline owns a gesture that cooperates with event drag/resize and vertical
  // scrolling. The other views use the sheet-level period gesture.
  const contentShouldSwipePeriods = viewMode !== 'timeline';

  return (
    <Animated.View
      style={[styles.motionSurface, fullScreen && styles.fullScreenSurface, sheetDragStyle]}
    >
      <View style={[styles.container, fullScreen && styles.fullScreenContainer, { paddingBottom: fullScreen ? bottomInset : Math.max(bottomInset, 12) }]}>
        <GestureDetector gesture={dragToDismissEnabled ? sheetDismissGesture : headerGesture}>
          <Animated.View style={styles.headerCard}>
            <TouchableOpacity
              onPress={onToggleSheet}
              activeOpacity={0.72}
              hitSlop={{ top: 12, bottom: 12, left: 120, right: 120 }}
              accessibilityRole="button"
              accessibilityLabel={t('calendar.toggleDayDetails', 'Toggle day details')}
              style={styles.grabberButton}
            >
              <Animated.View style={[styles.grabber, grabberAnimatedStyle]} />
            </TouchableOpacity>
            <Animated.View style={[styles.tooltip, tooltipAnimatedStyle]} pointerEvents="none">
              <Text style={styles.tooltipText}>{t('calendar.pullUpHint', 'Pull up to see day details')}</Text>
            </Animated.View>
            <Animated.View style={headerAnimatedStyle}>
              <View style={styles.headerContent}>
                <TouchableOpacity
                  onPress={() => (viewMode === 'week' ? onNavigateWeek(-1) : onNavigateDay(-1))}
                  activeOpacity={0.6}
                  hitSlop={12}
                  style={styles.navArrow}
                >
                  <Ionicons name="chevron-back" size={18} color={colors.textSecondary} />
                </TouchableOpacity>
                <View style={styles.headerCopy}>
                  <View style={styles.eyebrowRow}>
                    <Text style={styles.eyebrow}>{headerWeekdayLabel}</Text>
                    {relativeDay ? (
                      <View
                        style={[
                          styles.relativeDayBadge,
                          relativeDay.kind === 'today' && styles.relativeDayBadgeToday,
                          relativeDay.kind === 'tomorrow' && styles.relativeDayBadgeTomorrow,
                          relativeDay.kind === 'yesterday' && styles.relativeDayBadgeYesterday,
                        ]}
                        accessibilityLabel={relativeDay.label}
                      >
                        <Ionicons
                          name={relativeDay.icon}
                          size={10}
                          color={
                            relativeDay.kind === 'today'
                              ? colors.success
                              : relativeDay.kind === 'tomorrow'
                                ? colors.primary
                                : colors.warning
                          }
                        />
                        <Text
                          style={[
                            styles.relativeDayBadgeText,
                            relativeDay.kind === 'today' && styles.relativeDayBadgeTextToday,
                            relativeDay.kind === 'tomorrow' && styles.relativeDayBadgeTextTomorrow,
                            relativeDay.kind === 'yesterday' && styles.relativeDayBadgeTextYesterday,
                          ]}
                          numberOfLines={1}
                        >
                          {relativeDay.label}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                  <Text style={styles.title} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.78}>{headerTitle}</Text>
                </View>
                <View style={styles.headerActions}>
                  <View style={styles.countPill}>
                    <Text style={styles.countText}>{headerEventCount}</Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => onQuickAdd()}
                    activeOpacity={0.82}
                    hitSlop={10}
                    accessibilityRole="button"
                    accessibilityLabel={t('calendar.quickAdd', 'Quick add reminder')}
                    style={styles.quickAddButton}
                  >
                    <Ionicons name="add" size={19} color="#fff" />
                  </TouchableOpacity>
                </View>
                <TouchableOpacity
                  onPress={() => (viewMode === 'week' ? onNavigateWeek(1) : onNavigateDay(1))}
                  activeOpacity={0.6}
                  hitSlop={12}
                  style={styles.navArrow}
                >
                  <Ionicons name="chevron-forward" size={18} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
              <View style={styles.toggleRow}>
                <TouchableOpacity
                  onPress={() => handleViewModeChange('month')}
                  activeOpacity={0.85}
                  style={[styles.toggleButton, viewMode === 'month' && styles.toggleButtonActive]}
                >
                  <Ionicons name="list-outline" size={13} color={viewMode === 'month' ? '#fff' : colors.textSecondary} />
                  <Text style={[styles.toggleText, viewMode === 'month' && styles.toggleTextActive]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.82}>
                    {t('calendar.monthView', 'List')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => handleViewModeChange('timeline')}
                  activeOpacity={0.85}
                  style={[styles.toggleButton, viewMode === 'timeline' && styles.toggleButtonActive]}
                >
                  <Ionicons name="time-outline" size={13} color={viewMode === 'timeline' ? '#fff' : colors.textSecondary} />
                  <Text style={[styles.toggleText, viewMode === 'timeline' && styles.toggleTextActive]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.82}>
                    {t('calendar.dayView', 'Timeline')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => handleViewModeChange('week')}
                  activeOpacity={0.85}
                  style={[styles.toggleButton, viewMode === 'week' && styles.toggleButtonActive]}
                >
                  <Ionicons name="calendar-outline" size={13} color={viewMode === 'week' ? '#fff' : colors.textSecondary} />
                  <Text style={[styles.toggleText, viewMode === 'week' && styles.toggleTextActive]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.82}>
                    {t('calendar.weekView', 'Week')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => handleViewModeChange('agenda')}
                  activeOpacity={0.85}
                  style={[styles.toggleButton, viewMode === 'agenda' && styles.toggleButtonActive]}
                >
                  <Ionicons name="reader-outline" size={13} color={viewMode === 'agenda' ? '#fff' : colors.textSecondary} />
                  <Text style={[styles.toggleText, viewMode === 'agenda' && styles.toggleTextActive]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.82}>
                    {t('calendar.agendaView', 'Agenda')}
                  </Text>
                </TouchableOpacity>
              </View>
            </Animated.View>
          </Animated.View>
        </GestureDetector>

        {contentShouldSwipePeriods ? (
          <GestureDetector gesture={combinedContentSwipeGesture}>{detailContent}</GestureDetector>
        ) : detailContent}
      </View>
    </Animated.View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  motionSurface: {
    flex: 1,
    borderRadius: 24,
    backgroundColor: colors.background,
    ...(Platform.OS === 'ios'
      ? {
          shadowColor: '#08101D',
          shadowOpacity: 0.12,
          shadowRadius: 18,
          shadowOffset: { width: 0, height: 10 },
        }
      : {
          elevation: 8,
        }),
  },
  container: {
    flex: 1,
    borderRadius: 24,
    overflow: 'hidden',
    backgroundColor: colors.background,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  fullScreenSurface: {
    borderRadius: 0,
    elevation: 0,
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
  },
  fullScreenContainer: {
    borderRadius: 0,
    borderWidth: 0,
  },
  fullScreenContentWrap: {
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  fullScreenContentSurface: {
    borderRadius: 0,
    borderWidth: 0,
  },
  headerCard: {
    backgroundColor: 'transparent',
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 6,
  },
  grabberButton: {
    alignItems: 'center',
  },
  grabber: {
    alignSelf: 'center',
    width: 46,
    height: 5,
    borderRadius: 999,
    backgroundColor: colors.primary + '45',
    marginBottom: 7,
  },
  tooltip: {
    position: 'absolute',
    top: -28,
    alignSelf: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  tooltipText: {
    color: '#fff',
    fontSize: 11,
    fontFamily: 'Inter_600SemiBold',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 38,
  },
  navArrow: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface + '99',
  },
  headerCopy: {
    flex: 1,
    marginHorizontal: 8,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: 9,
    fontFamily: 'Inter_700Bold',
    textTransform: 'uppercase',
    letterSpacing: 1.6,
    color: colors.textDim,
  },
  eyebrowRow: {
    minHeight: 18,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  relativeDayBadge: {
    minHeight: 17,
    maxWidth: 104,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 1,
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
  },
  relativeDayBadgeToday: {
    backgroundColor: colors.success + '18',
    borderColor: colors.success + '45',
  },
  relativeDayBadgeTomorrow: {
    backgroundColor: colors.primary + '14',
    borderColor: colors.primary + '42',
  },
  relativeDayBadgeYesterday: {
    backgroundColor: colors.warning + '16',
    borderColor: colors.warning + '48',
  },
  relativeDayBadgeText: {
    flexShrink: 1,
    fontSize: 9,
    lineHeight: 11,
    fontFamily: 'Inter_700Bold',
  },
  relativeDayBadgeTextToday: {
    color: colors.success,
  },
  relativeDayBadgeTextTomorrow: {
    color: colors.primary,
  },
  relativeDayBadgeTextYesterday: {
    color: colors.warning,
  },
  title: {
    fontSize: 19,
    fontFamily: 'Inter_400Regular',
    color: colors.text,
    letterSpacing: 0.15,
  },
  countPill: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 2,
    ...(Platform.OS === 'ios'
      ? {
          shadowColor: '#0a1628',
          shadowOpacity: 0.15,
          shadowRadius: 8,
          shadowOffset: { width: 0, height: 3 },
        }
      : {}),
  },
  countText: {
    fontSize: 13,
    fontFamily: 'Inter_700Bold',
    color: '#fff',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  quickAddButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 2,
    ...(Platform.OS === 'ios'
      ? {
          shadowColor: '#0a1628',
          shadowOpacity: 0.14,
          shadowRadius: 8,
          shadowOffset: { width: 0, height: 3 },
        }
      : {}),
  },
  toggleRow: {
    marginTop: 7,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    padding: 3,
    borderRadius: 18,
    backgroundColor: colors.surface + '99',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  toggleButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    minWidth: 0,
    paddingHorizontal: 5,
    paddingVertical: 7,
    borderRadius: 15,
    backgroundColor: 'transparent',
    borderWidth: 0,
  },
  toggleButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  toggleText: {
    flexShrink: 1,
    fontSize: 11,
    fontFamily: 'Inter_700Bold',
    color: colors.textSecondary,
    letterSpacing: 0,
  },
  toggleTextActive: {
    color: '#fff',
  },
  contentWrap: {
    flex: 1,
    paddingHorizontal: 12,
    paddingTop: 2,
    paddingBottom: 12,
    ...(Platform.OS === 'web' ? { touchAction: 'pan-y' } : {}),
  } as any,
  viewTransitionContent: {
    flex: 1,
  },
  timelineWrap: {
    flex: 1,
    overflow: 'hidden',
    borderRadius: 20,
    backgroundColor: colors.surface,
    paddingTop: 10,
    paddingHorizontal: 4,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  eventsList: {
    flex: 1,
    borderRadius: 20,
    backgroundColor: colors.surface,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 4,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Inter_700Bold',
    color: colors.textDim,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  listContent: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    paddingHorizontal: 10,
    paddingTop: 16,
    paddingBottom: 32,
  },
});
